import React, {useEffect, useRef, useState} from "react";

export default function HorizontalScrollbar({scrollRef}) {
  const trackRef = useRef(null);
  const rafRef = useRef(0);

  const [meta, setMeta] = useState({
    show: false, thumbLeft: 0, thumbWidth: 90,
  });

  const calc = () => {
    const el = scrollRef?.current;
    const track = trackRef.current;
    if (!el || !track) return;

    const {scrollWidth, clientWidth, scrollLeft} = el;

    const overflow = scrollWidth > clientWidth + 1;
    if (!overflow) {
      setMeta({show: false, thumbLeft: 0, thumbWidth: 90});
      return;
    }

    const trackW = track.clientWidth || 1;
    const ratio = clientWidth / scrollWidth;

    const thumbW = Math.max(90, Math.floor(trackW * ratio));
    const maxLeft = Math.max(0, trackW - thumbW);
    const maxScroll = Math.max(1, scrollWidth - clientWidth);

    const left = Math.round((scrollLeft / maxScroll) * maxLeft);

    setMeta({show: true, thumbLeft: left, thumbWidth: thumbW});
  };

  useEffect(() => {
    const el = scrollRef?.current;
    const track = trackRef.current;
    if (!el || !track) return;

    const schedule = () => {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(calc);
    };

    schedule();
    rafRef.current = requestAnimationFrame(schedule);

    const onScroll = () => schedule();
    el.addEventListener("scroll", onScroll, {passive: true});

    const ro = new ResizeObserver(schedule);
    ro.observe(el);
    ro.observe(track);

    window.addEventListener("resize", schedule);

    return () => {
      el.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", schedule);
      ro.disconnect();
      cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollRef]);

  const startDrag = (e) => {
    if (!meta.show) return;

    const el = scrollRef?.current;
    const track = trackRef.current;
    if (!el || !track) return;

    e.preventDefault();

    const pointerX = "touches" in e ? e.touches[0].clientX : e.clientX;
    const startX = pointerX;
    const startLeft = meta.thumbLeft;

    const trackW = track.clientWidth;
    const maxLeft = Math.max(0, trackW - meta.thumbWidth);
    const maxScroll = Math.max(1, el.scrollWidth - el.clientWidth);

    const move = (ev) => {
      const x = "touches" in ev ? ev.touches[0].clientX : ev.clientX;
      const dx = x - startX;

      const nextLeft = Math.min(maxLeft, Math.max(0, startLeft + dx));
      const nextScrollLeft = (nextLeft / Math.max(1, maxLeft)) * maxScroll;

      el.scrollLeft = nextScrollLeft;
    };

    const up = () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
      window.removeEventListener("touchmove", move);
      window.removeEventListener("touchend", up);
    };

    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    window.addEventListener("touchmove", move, {passive: false});
    window.addEventListener("touchend", up);
  };

  return (<div
      ref={trackRef}
      className="h-3 bg-[#A6AAB8] relative rounded-full"
      style={{
        visibility: meta.show ? "visible" : "hidden",
      }}
    >
      <div
        role="slider"
        aria-label="Horizontal scroll"
        className="absolute top-1/2 -translate-y-1/2 h-3 bg-[#E9ECF3] rounded-full cursor-pointer"
        style={{left: meta.thumbLeft, width: meta.thumbWidth}}
        onMouseDown={startDrag}
        onTouchStart={startDrag}
      />
    </div>);
}